import axios from 'axios';

const resolveApiUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    return envUrl;
  }

  if (typeof window === 'undefined') {
    return 'http://localhost:8080';
  }

  const { protocol, hostname } = window.location;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
  if (isLocalhost) {
    return 'http://localhost:8080';
  }

  if (hostname.startsWith('admin.') || hostname.startsWith('rezervacia.')) {
    const rootDomain = hostname.replace(/^(admin|rezervacia)\./, '');
    return `${protocol}//${rootDomain}`;
  }

  return window.location.origin;
};

const API_URL = resolveApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const csrfToken = localStorage.getItem('csrf_token');
    if (csrfToken && ['post', 'put', 'delete'].includes(config.method)) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, config } = error.response;
      const isLoginRequest = config.url?.includes('/api/admin/login');
      const isSessionRequest = config.url?.includes('/api/admin/session');
      const isAdminPage = window.location.pathname.startsWith('/admin');
      
      if ((status === 401 || status === 403) && !isLoginRequest && !isSessionRequest && isAdminPage) {
        console.warn('Session expired or unauthorized. Redirecting to login...');
        localStorage.removeItem('csrf_token');
        localStorage.removeItem('admin_user');
        if (!window.location.pathname.includes('/admin/login')) {
          window.location.href = '/admin/login';
        }
      }
      
      if (status === 404 && config.url?.includes('/api/admin/') && !isLoginRequest && !isSessionRequest && isAdminPage) {
        console.warn('Admin endpoint returned 404. Checking session...');
        api.get('/api/admin/dashboard').catch(() => {
          console.warn('Session check failed. Redirecting to login...');
          localStorage.removeItem('csrf_token');
          localStorage.removeItem('admin_user');
          if (!window.location.pathname.includes('/admin/login')) {
            window.location.href = '/admin/login';
          }
        });
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Získa zoznam služieb
 */
export const getServices = () => {
  return api.get('/api/services');
};

/**
 * Získa dostupné časové sloty
 * @param {string} date - Dátum (YYYY-MM-DD)
 * @param {number} serviceId - ID služby
 */
export const getAvailableSlots = (date, serviceId) => {
  return api.get('/api/available-slots', {
    params: { date, service_id: serviceId }
  });
};

/**
 * Vytvorí novú rezerváciu
 * @param {Object} data - Dáta rezervácie
 */
export const createReservation = (data) => {
  return api.post('/api/reservations', data);
};

/**
 * Nájde rezerváciu podľa ID a emailu
 * @param {string} rezervacneId - Rezervačné číslo
 * @param {string} email - Email zákazníka
 */
export const getReservationByIdAndEmail = (rezervacneId, email) => {
  return api.post('/api/reservations/find', { rezervacneId, email });
};

/**
 * Zruší rezerváciu
 * @param {number} id - ID rezervácie
 */
export const cancelReservationById = (id) => {
  return api.post(`/api/reservations/${id}/cancel`);
};

/**
 * Zruší rezerváciu cez token
 * @param {string} token - Cancel token
 */
export const cancelReservationByToken = (token) => {
  return api.get(`/api/reservations/cancel/${token}`);
};

/**
 * Health check
 */
export const healthCheck = () => {
  return api.get('/api/health');
};

/**
 * Získa verejnú galériu
 */
export const getGallery = () => {
  return api.get('/api/gallery');
};

/**
 * Získa návrhy adries cez backend proxy na Photon
 * @param {string} query - Hľadaná adresa
 */
export const searchAddresses = (query) => {
  return api.get('/api/address-search', {
    params: { q: query }
  });
};

// ============= ADMIN API =============

/**
 * Admin login
 * @param {Object} credentials - { username, password }
 */
export const adminLogin = (credentials) => {
  return api.post('/api/admin/login', credentials).then(response => {
    const data = response.data.data || response.data;
  
    if (data.csrf_token) {
      localStorage.setItem('csrf_token', data.csrf_token);
    }
    if (data.user) {
      localStorage.setItem('admin_user', JSON.stringify(data.user));
    }
    window.dispatchEvent(new Event('admin-session-updated'));
    return response;
  });
};

/**
 * Admin logout
 */
export const adminLogout = () => {
  return api.get('/api/admin/logout').then(response => {
    localStorage.removeItem('csrf_token');
    localStorage.removeItem('admin_user');
    window.dispatchEvent(new Event('admin-session-cleared'));
    return response;
  });
};

/**
 * Admin session info
 */
export const getAdminSession = () => {
  return api.get('/api/admin/session');
};

/**
 * Refresh admin session
 */
export const pingAdminSession = () => {
  return api.get('/api/admin/ping');
};

/**
 * Get recent activities
 * @param {number|null} limit - Number of activities to fetch; null = all
 */
export const getRecentActivities = (limit = null) => {
  return api.get('/api/admin/activities', {
    params: limit ? { limit } : {}
  });
};

/**
 * Získa CSRF token
 */
export const getCsrfToken = () => {
  return api.get('/api/admin/csrf-token').then(response => {
    if (response.data.csrf_token) {
      localStorage.setItem('csrf_token', response.data.csrf_token);
    }
    return response;
  });
};

/**
 * Získa dashboard data
 */
export const getDashboard = () => {
  return api.get('/api/admin/dashboard');
};

/**
 * Získa galériu (admin)
 */
export const getAdminGallery = () => {
  return api.get('/api/admin/gallery');
};

/**
 * Nahrá fotky do galérie (admin)
 * @param {FormData} formData
 */
export const uploadGalleryImages = (formData) => {
  return api.post('/api/admin/gallery', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    transformRequest: (data, headers) => {
      delete headers['Content-Type'];
      return data;
    }
  });
};

/**
 * Zmaže fotku z galérie (admin)
 * @param {string} filename
 */
export const deleteGalleryImage = (filename) => {
  return api.delete(`/api/admin/gallery/${encodeURIComponent(filename)}`);
};

/**
 * Získa zoznam rezervácií
 * @param {Object} params - Query parametre (page, limit, status, ...)
 */
export const getReservations = (params = {}) => {
  return api.get('/api/admin/reservations', { params });
};

/**
 * Získa detail rezervácie
 * @param {number} id - ID rezervácie
 */
export const getReservation = (id) => {
  return api.get(`/api/admin/reservations/${id}`);
};

/**
 * Vytvorí novú rezerváciu (admin)
 * @param {Object} data - Dáta rezervácie
 */
export const createReservationAdmin = (data) => {
  return api.post('/api/admin/reservations', data);
};

/**
 * Aktualizuje rezerváciu (admin)
 * @param {number} id - ID rezervácie
 * @param {Object} data - Nové dáta
 */
export const updateReservation = (id, data) => {
  return api.put(`/api/admin/reservations/${id}`, data);
};

/**
 * Aktualizuje rezerváciu (customer/public)
 * @param {number} id - ID rezervácie
 * @param {Object} data - Nové dáta
 */
export const updateReservationByCustomer = (id, data) => {
  return api.put(`/api/reservations/${id}`, data);
};

/**
 * Zmaže rezerváciu
 * @param {number} id - ID rezervácie
 */
export const deleteReservation = (id) => {
  return api.delete(`/api/admin/reservations/${id}`);
};

/**
 * Potvrdí rezerváciu (admin)
 * @param {number} id - ID rezervácie
 */
export const confirmReservationAdmin = (id) => {
  return api.post(`/api/admin/reservations/${id}/confirm`);
};

/**
 * Zruší rezerváciu (admin)
 * @param {number} id - ID rezervácie
 */
export const cancelReservationAdmin = (id) => {
  return api.post(`/api/admin/reservations/${id}/cancel`);
};

/**
 * Získa zoznam služieb (admin)
 */
export const getServicesAdmin = () => {
  return api.get('/api/admin/services');
};

/**
 * Vytvorí novú službu
 * @param {Object} data - Dáta služby
 */
export const createService = (data) => {
  return api.post('/api/admin/services', data);
};

/**
 * Aktualizuje službu
 * @param {number} id - ID služby
 * @param {Object} data - Nové dáta
 */
export const updateService = (id, data) => {
  return api.put(`/api/admin/services/${id}`, data);
};

/**
 * Zmaže službu
 * @param {number} id - ID služby
 */
export const deleteService = (id) => {
  return api.delete(`/api/admin/services/${id}`);
};

/**
 * Aktualizuje poradie služieb
 * @param {Array} orderedIds - Pole ID služieb v novom poradí
 */
export const updateServiceOrder = (orderedIds) => {
  return api.post('/api/admin/services/reorder', { order: orderedIds });
};

/**
 * Získa zoznam adminov
 */
export const getAdminUsers = () => {
  return api.get('/api/admin/users');
};

/**
 * Vytvorí nového admina
 * @param {Object} data - Dáta admina
 */
export const createAdminUser = (data) => {
  return api.post('/api/admin/users', data);
};

/**
 * Aktualizuje admina
 * @param {number} id - ID admina
 * @param {Object} data - Nové dáta
 */
export const updateAdminUser = (id, data) => {
  return api.put(`/api/admin/users/${id}`, data);
};

/**
 * Deaktivuje admina
 * @param {number} id - ID admina
 */
export const deleteAdminUser = (id) => {
  return api.delete(`/api/admin/users/${id}`);
};

/**
 * Resetuje heslo admina
 * @param {number} id - ID admina
 * @param {Object} data - { heslo }
 */
export const resetAdminPassword = (id, data) => {
  return api.post(`/api/admin/users/${id}/reset-password`, data);
};

/**
 * Získa zoznam blokovaných časov
 * @param {Object} params - Query parametre
 */
export const getBlockedTimes = (params = {}) => {
  return api.get('/api/admin/blocked-times', { params });
};

/**
 * Vytvorí blokovaný čas
 * @param {Object} data - Dáta
 */
export const createBlockedTime = (data) => {
  return api.post('/api/admin/blocked-times', data);
};

/**
 * Aktualizuje blokovaný čas
 * @param {number} id - ID
 * @param {Object} data - Nové dáta
 */
export const updateBlockedTime = (id, data) => {
  return api.put(`/api/admin/blocked-times/${id}`, data);
};

/**
 * Zmaže blokovaný čas
 * @param {number} id - ID
 */
export const deleteBlockedTime = (id) => {
  return api.delete(`/api/admin/blocked-times/${id}`);
};

/**
 * Získa systémové info
 */
export const getSystemInfo = () => {
  return api.get('/api/admin/system/info');
};

/**
 * Získa nastavenia
 */
export const getSettings = () => {
  return api.get('/api/admin/settings');
};

/**
 * Aktualizuje nastavenia
 * @param {Object} data - Nastavenia
 */
export const updateSettings = (data) => {
  return api.put('/api/admin/settings', data);
};

/**
 * Získa používateľské nastavenia
 */
export const getUserSettings = () => {
  return api.get('/api/admin/user-settings');
};

/**
 * Aktualizuje používateľské nastavenia
 * @param {Object} data - Používateľské nastavenia
 */
export const updateUserSettings = (data) => {
  return api.put('/api/admin/user-settings', data);
};

/**
 * Získa email šablónu
 * @param {string} type - Typ šablóny
 */
export const getEmailTemplate = (type) => {
  return api.get(`/api/admin/settings/email-template/${type}`);
};

/**
 * Pridá prestávku
 * @param {Object} data - Dáta prestávky
 */
export const addBreak = (data) => {
  return api.post('/api/admin/settings/breaks', data);
};

/**
 * Zmaže prestávku
 * @param {number} id - ID prestávky
 */
export const deleteBreak = (id) => {
  return api.delete(`/api/admin/settings/breaks/${id}`);
};

/**
 * Získa verejné nastavenia (pre frontend zobrazenie)
 */
export const getPublicSettings = () => {
  return api.get('/api/settings/public');
};

/**
 * Získa emailové šablóny
 */
export const getEmailTemplates = () => {
  return api.get('/api/admin/email-templates');
};

/**
 * Aktualizuje emailovú šablónu
 * @param {number} id - ID šablóny
 * @param {object} data - Dáta šablóny
 */
export const updateEmailTemplate = (id, data) => {
  return api.put(`/api/admin/email-templates/${id}`, data);
};

/**
 * Google Calendar API
 */

/**
 * Získa stav pripojenia Google Calendar
 */
export const getGoogleCalendarStatus = () => {
  return api.get('/api/admin/google-calendar/status');
};

/**
 * Odpojí Google Calendar
 */
export const disconnectGoogleCalendar = () => {
  return api.post('/api/admin/google-calendar/disconnect');
};

/**
 * Spustí manuálnu synchronizáciu
 */
export const syncGoogleCalendar = () => {
  return api.post('/api/admin/google-calendar/sync');
};

export default api;
